import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm } from 'react-hook-form';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

interface CancelTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
}

interface CancelTaskFormData {
  reason: string;
}

export function CancelTaskModal({ isOpen, onClose, taskId }: CancelTaskModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CancelTaskFormData>({
    defaultValues: {
      reason: '',
    },
  });

  const cancelTaskMutation = trpcAdmin.support.task.cancel.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: t`Task cancelled successfully`,
      });
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      reset();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to cancel task`,
        description: error.message,
      });
    },
  });

  const onSubmit = (data: CancelTaskFormData) => {
    cancelTaskMutation.mutate({
      taskId,
      reason: data.reason,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <Trans>Cancel Task</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired isInvalid={!!errors.reason}>
              <FormLabel>
                <Trans>Reason for cancellation</Trans>
              </FormLabel>
              <Textarea
                {...register('reason', {
                  required: t`Please provide a reason for cancellation`,
                })}
                placeholder={t`Explain why this task is being cancelled...`}
                rows={4}
              />
              {errors.reason ? <FormErrorMessage>{errors.reason.message}</FormErrorMessage> : null}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Close</Trans>
            </Button>
            <Button
              type="submit"
              colorScheme="red"
              isLoading={isSubmitting || cancelTaskMutation.isPending}
            >
              <Trans>Cancel Task</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
